import { formatDate } from '@angular/common';

import { ChartInformation, DateRange, FullDateRange, GuidelineStats, TrendingStats, UserStats } from '@treetop/shared/model';

export type StatisticsKeyNumberTuple = {
  translationKey: string;
  number: number;
};

export const convertGuidelineStatsToList = (guidelineStats: GuidelineStats): StatisticsKeyNumberTuple[] => {
  return [
    { translationKey: 'Statistics.TotalCount', number: guidelineStats.totalCount },
    { translationKey: 'Statistics.PublishedCount', number: guidelineStats.publishedCount },
    { translationKey: 'Statistics.InCreationCount', number: guidelineStats.inCreationCount },
    { translationKey: 'Statistics.UpdatingCount', number: guidelineStats.updatingCount },
  ];
};

export const convertUserStatsToList = (userStats: UserStats): StatisticsKeyNumberTuple[] => {
  return [
    {
      translationKey: 'Statistics.UsersLoggedInLastDays',
      number: userStats.usersLoggedInLastDays,
    },
  ];
};

/**
 * Formats a picked date as the calendar day the user sees.
 *
 * The date pickers produce local dates - a range start is local midnight - while the API takes a
 * DateOnly. Reading UTC components would shift that start to the previous day for anyone east of
 * UTC, widening the requested period by a day and skewing the per-day averages, so the local
 * components are the ones to send.
 */
export const convertISOToDateOnly = (date: Date): string => {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0'); // Months are zero-indexed
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const generateChartInformation = (
  trends: TrendingStats,
  labelTranslationKey: string,
  fullDateRange: FullDateRange,
): ChartInformation => {
  return {
    fullDateRange: fullDateRange,
    currentPeriodCount: trends?.currentPeriodCount,
    previousPeriodCount: trends?.previousPeriodCount,
    currentPeriodAverage: trends?.currentPeriodAverage ?? 0,
    previousPeriodAverage: trends?.previousPeriodAverage ?? 0,
    labels: trends?.trendingRows.map((row) => formatDate(row.date, 'dd.MM', 'en-US')) ?? [],
    datasets: {
      labelTranslationKey: labelTranslationKey,
      data: trends?.trendingRows.map((row) => row.value) ?? [],
      backgroundColor: '#2987DE',
      borderColor: '#2987DE',
      borderWidth: 1,
    },
  };
};

export const getDifferenceInDates = (start: Date, end: Date): { translationKey: string; value: number | string } => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const startDate = new Date(start);
  startDate.setHours(0, 0, 0, 0);
  const endDate = new Date(end);
  endDate.setHours(0, 0, 0, 0);

  const lastYearStart = new Date(today.getFullYear() - 1, 0, 1);
  const lastYearEnd = new Date(today.getFullYear() - 1, 11, 31);
  const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
  const startOfWeek = new Date(today);
  startOfWeek.setDate(today.getDate() - today.getDay());

  if (startDate.getTime() === lastYearStart.getTime() && endDate.getTime() === lastYearEnd.getTime()) {
    return { translationKey: 'Statistics.LastYear', value: 1 };
  } else if (startDate.getTime() === lastMonthStart.getTime() && endDate.getTime() === lastMonthEnd.getTime()) {
    return { translationKey: 'Statistics.LastMonth', value: 1 };
  }

  const diffTime = endDate.getTime() - startDate.getTime();
  const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

  if (endDate.getTime() === today.getTime() && today.getTime() !== lastMonthEnd.getTime()) {
    return { translationKey: 'Statistics.LastXDays', value: diffDays + 1 };
  }

  return {
    translationKey: 'Statistics.InPeriod',
    value: `${formatDate(startDate, 'dd.MM.yy', 'en-US')} - ${formatDate(endDate, 'dd.MM.yy', 'en-US')}`,
  };
};

export const getDifferenceInDays = (start: Date, end: Date): string => {
  if (!start || !end) {
    return null;
  }

  const startDate = new Date(start);
  const endDate = new Date(end);

  startDate.setHours(0, 0, 0, 0);
  endDate.setHours(0, 0, 0, 0);

  const MS_PER_DAY = 1000 * 60 * 60 * 24;
  const utcDate1 = Date.UTC(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
  const utcDate2 = Date.UTC(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
  const differenceInDays = Math.floor((utcDate2 - utcDate1) / MS_PER_DAY) + 1;

  return `${differenceInDays}`;
};

export const calculatePreviousPeriod = (dateRange: DateRange): DateRange => {
  const startDate = new Date(dateRange.start);
  const endDate = new Date(dateRange.end);

  // Normalize both dates to midnight for whole-day calculations
  startDate.setHours(0, 0, 0, 0);
  endDate.setHours(0, 0, 0, 0);

  const isFirstDayOfMonth = startDate.getDate() === 1;
  const lastDayInEndMonth = new Date(endDate.getFullYear(), endDate.getMonth() + 1, 0).getDate();
  const isLastDayOfMonth = endDate.getDate() === lastDayInEndMonth;
  const totalMonths = (endDate.getFullYear() - startDate.getFullYear()) * 12 + (endDate.getMonth() - startDate.getMonth()) + 1;

  if (isFirstDayOfMonth && isLastDayOfMonth && totalMonths > 0) {
    const prevStart = new Date(startDate.getFullYear(), startDate.getMonth() - totalMonths, 1);
    const prevEnd = new Date(prevStart.getFullYear(), prevStart.getMonth() + totalMonths, 0);

    return {
      start: prevStart,
      end: prevEnd,
    };
  } else {
    // Fallback: day-based logic
    const dayDiff = Math.floor((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
    const inclusiveDays = dayDiff + 1;

    const previousEndDate = new Date(startDate);
    previousEndDate.setDate(previousEndDate.getDate() - 1);

    const previousStartDate = new Date(previousEndDate);
    previousStartDate.setDate(previousStartDate.getDate() - (inclusiveDays - 1));

    return {
      start: previousStartDate,
      end: previousEndDate,
    };
  }
};

export const getComparedPercentageText = (current: number, previous: number) => {
  const percentage = ((current - previous) / previous) * 100;

  if (isNaN(percentage)) {
    return '';
  }

  if (percentage === 0) {
    return '0%';
  }

  const isIncreased = percentage > 0;
  const adjustedPercentage = Math.abs(percentage).toFixed(2);

  if (adjustedPercentage === 'Infinity') {
    return '';
  }

  return `${isIncreased ? '+' : '-'} ${adjustedPercentage}%`;
};
